const SITE_HOST    = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH    = '/sites/DefineHolyFamily';
const GRAPH        = 'https://graph.microsoft.com/v1.0';
const LIBRARY_NAME = 'FamilyVault_Photos';

let cachedSiteId  = null;
let cachedDriveId = null;

const COLUMN_DEFS = [
  { name: 'DateTaken', displayName: 'DateTaken', dateTime: {} },
  { name: 'People',    displayName: 'People',    text: { allowMultipleLines: true } },
  { name: 'Project',   displayName: 'Project',   text: {} },
  { name: 'Tags',      displayName: 'Tags',       text: { allowMultipleLines: true } },
  { name: 'Location',  displayName: 'Location',  text: {} },
  { name: 'Caption',   displayName: 'Caption',   text: { allowMultipleLines: true } },
  { name: 'Source',    displayName: 'Source',    text: {} },
  { name: 'Favorite',  displayName: 'Favorite',  boolean: {} },
  { name: 'Witness',   displayName: 'Witness',   text: { allowMultipleLines: true } },
  { name: 'Doctrines', displayName: 'Doctrines', text: {} },
  { name: 'Tier',      displayName: 'Tier',      choice: {
    allowTextEntry: false,
    choices: ['Archive', 'Keeper', 'Treasure'],
    displayAs: 'dropDownMenu',
  }},
];

// ── Auth helpers ──────────────────────────────────────────────────────────────

async function graphGet(token, path) {
  const res = await fetch(`${GRAPH}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Graph GET ${path} → ${res.status}: ${await res.text()}`);
  return res.json();
}

async function resolveSiteId(token) {
  if (cachedSiteId) return cachedSiteId;
  const data = await graphGet(token, `/sites/${SITE_HOST}:${SITE_PATH}:`);
  cachedSiteId = data.id;
  return cachedSiteId;
}

// ── Library resolution + auto-creation ───────────────────────────────────────

async function scanForDrive(token, siteId) {
  const data = await graphGet(token, `/sites/${siteId}/drives?$select=name,id&$top=100`);
  const drive = (data.value || []).find(d => d.name === LIBRARY_NAME);
  return drive ? drive.id : null;
}

async function addColumns(token, siteId, listId) {
  for (const col of COLUMN_DEFS) {
    try {
      await fetch(`${GRAPH}/sites/${siteId}/lists/${listId}/columns`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(col),
      });
    } catch { /* column add is non-fatal */ }
  }
}

async function createPhotoLibrary(token, siteId) {
  const res = await fetch(`${GRAPH}/sites/${siteId}/lists`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      displayName: LIBRARY_NAME,
      list: { template: 'documentLibrary' },
    }),
  });
  if (!res.ok && res.status !== 409) {
    throw new Error(`Failed to create ${LIBRARY_NAME} (${res.status}): ${await res.text()}`);
  }

  // Give SharePoint a moment to provision
  await new Promise(r => setTimeout(r, 1500));

  const driveId = await scanForDrive(token, siteId);
  if (!driveId) throw new Error(`${LIBRARY_NAME} created but drive not found in site`);

  // Find list ID so we can add columns
  const listRes = await fetch(
    `${GRAPH}/sites/${siteId}/lists?$filter=displayName eq '${LIBRARY_NAME}'&$select=id&$top=5`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (listRes.ok) {
    const listData = await listRes.json();
    const list = (listData.value || [])[0];
    if (list) await addColumns(token, siteId, list.id);
  }

  return driveId;
}

async function resolveDriveId(token) {
  if (cachedDriveId) return cachedDriveId;
  const siteId  = await resolveSiteId(token);
  const driveId = await scanForDrive(token, siteId)
    || await createPhotoLibrary(token, siteId);
  cachedDriveId = driveId;
  return cachedDriveId;
}

// ── Shape normalisation ───────────────────────────────────────────────────────

function mapItem(driveItem) {
  const fields = driveItem.listItem?.fields || {};
  const thumbs = driveItem.thumbnails?.[0] || {};
  const largeUrl  = thumbs.large?.url  || null;
  const mediumUrl = thumbs.medium?.url || null;

  return {
    id:           driveItem.id,
    name:         driveItem.name,
    webUrl:       driveItem.webUrl,
    // Full-res: pre-authenticated download URL when available, else large thumb
    fullResUrl:   driveItem['@microsoft.graph.downloadUrl'] || largeUrl || null,
    // Grid thumbnail: prefer medium for performance, fall back to large
    thumbnailUrl: mediumUrl || largeUrl || null,
    size:                driveItem.size,
    createdDateTime:     driveItem.createdDateTime,
    lastModifiedDateTime: driveItem.lastModifiedDateTime,
    hasCustomTitle: !!fields.Title,
    // Canonical field names (written to by updatePhotoMetadata)
    DateTaken: fields.DateTaken   || null,
    People:    fields.People      || null,
    Project:   fields.Project     || null,
    Tags:      fields.Tags        || null,
    Location:  fields.Location    || null,
    Caption:   fields.Caption     || null,
    Source:    fields.Source      || null,
    Favorite:  fields.Favorite    || false,
    // --- Compatibility aliases so Photos.js + ItemDetail.js need minimal changes ---
    Title:          fields.Title          || driveItem.name.replace(/\.[^.]+$/, ''),
    MediaType:      'Photo',
    DateOfEvent:    fields.DateTaken      || null,   // sort/display alias
    AlbumCollection: fields.Project       || null,   // filter alias
    Notes:          fields.Caption        || null,   // edit section alias
    Created:        driveItem.createdDateTime,
    Witness:        fields.Witness        || null,
    Doctrines:      fields.Doctrines      || null,
    Tier:           fields.Tier           || null,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function listPhotos(token) {
  const driveId = await resolveDriveId(token);
  // No $select — ensures @microsoft.graph.downloadUrl annotation is returned
  const expand  = 'thumbnails,listItem($expand=fields)';
  const data    = await graphGet(token,
    `/drives/${driveId}/root/children?$expand=${expand}&$top=500`);

  return (data.value || [])
    .filter(i => i.file) // exclude any subfolders
    .map(mapItem);
}

export async function getPhoto(token, photoId) {
  const driveId = await resolveDriveId(token);
  const expand  = 'thumbnails,listItem($expand=fields)';
  let res;
  try {
    res = await graphGet(token, `/drives/${driveId}/items/${photoId}?$expand=${expand}`);
  } catch (e) {
    if (e.message.startsWith('Graph GET') && e.message.includes('404')) return null;
    throw e;
  }
  return mapItem(res);
}

// fields keys: any of Title, DateOfEvent, AlbumCollection, People, Tags,
//              Location, Notes, Caption, Source, Favorite, Project
export async function updatePhotoMetadata(token, photoId, fields) {
  const driveId = await resolveDriveId(token);

  // Map display/compat aliases → actual SP column names
  const sp = {};
  if (fields.Title           != null) sp.Title    = fields.Title;
  if (fields.DateOfEvent     != null) sp.DateTaken = fields.DateOfEvent;
  if (fields.DateTaken       != null) sp.DateTaken = fields.DateTaken;
  if (fields.AlbumCollection != null) sp.Project   = fields.AlbumCollection;
  if (fields.Project         != null) sp.Project   = fields.Project;
  if (fields.People          != null) sp.People    = fields.People;
  if (fields.Tags            != null) sp.Tags      = fields.Tags;
  if (fields.Location        != null) sp.Location  = fields.Location;
  if (fields.Caption         != null) sp.Caption   = fields.Caption;
  if (fields.Notes           != null) sp.Caption   = fields.Notes;
  if (fields.Source          != null) sp.Source    = fields.Source;
  if (fields.Favorite        != null) sp.Favorite  = fields.Favorite;
  if (fields.Witness         != null) sp.Witness   = fields.Witness;
  if (fields.Doctrines       != null) sp.Doctrines = fields.Doctrines;
  if (fields.Tier !== undefined) sp.Tier = fields.Tier === '' ? null : fields.Tier;

  const res = await fetch(
    `${GRAPH}/drives/${driveId}/items/${photoId}/listItem/fields`,
    {
      method:  'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify(sp),
    }
  );
  if (!res.ok) throw new Error(`updatePhotoMetadata ${photoId} → ${res.status}: ${await res.text()}`);
  return res.json();
}

// Uploads a File to the library root and writes metadata columns.
// Uses @microsoft.graph.conflictBehavior=rename so SP auto-suffixes the
// filename on collision rather than silently overwriting existing files.
export async function uploadPhoto(token, file, metadata) {
  const driveId = await resolveDriveId(token);

  const uploadRes = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(file.name)}:/content?@microsoft.graph.conflictBehavior=rename`,
    {
      method:  'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'application/octet-stream' },
      body:    file,
    }
  );
  if (!uploadRes.ok) {
    throw new Error(`Upload failed for "${file.name}" (${uploadRes.status}): ${await uploadRes.text()}`);
  }
  const driveItem = await uploadRes.json();

  const sp = {};
  if (metadata.Title)          sp.Title    = metadata.Title;
  if (metadata.DateTaken)      sp.DateTaken = metadata.DateTaken;
  if (metadata.People)         sp.People   = metadata.People;
  if (metadata.Project)        sp.Project  = metadata.Project;
  if (metadata.Tags)           sp.Tags     = metadata.Tags;
  if (metadata.Location)       sp.Location = metadata.Location;
  if (metadata.Caption)        sp.Caption  = metadata.Caption;
  if (metadata.Source)         sp.Source    = metadata.Source;
  if (metadata.Favorite != null) sp.Favorite = metadata.Favorite;
  if (metadata.Witness)        sp.Witness   = metadata.Witness;
  if (metadata.Doctrines)      sp.Doctrines = metadata.Doctrines;
  if (metadata.Tier)           sp.Tier      = metadata.Tier;

  if (Object.keys(sp).length > 0) {
    const patchRes = await fetch(
      `${GRAPH}/drives/${driveId}/items/${driveItem.id}/listItem/fields`,
      {
        method:  'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify(sp),
      }
    );
    if (!patchRes.ok) {
      throw new Error(`Metadata write failed for "${driveItem.name}" (${patchRes.status}): ${await patchRes.text()}`);
    }
  }

  return getPhoto(token, driveItem.id);
}

// Moves the item to the SharePoint recycle bin (not a hard delete).
// For SharePoint document libraries, Graph DELETE goes to recycle bin.
export async function deletePhoto(token, photoId) {
  const driveId = await resolveDriveId(token);
  const res = await fetch(`${GRAPH}/drives/${driveId}/items/${photoId}`, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  // 204 = success; anything else is an error
  if (res.status !== 204 && !res.ok) {
    throw new Error(`deletePhoto ${photoId} → ${res.status}: ${await res.text()}`);
  }
}
